import React, { useEffect, useReducer } from 'react';
import get from 'lodash/get';
import { Box, Grid, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import moment from 'moment';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';

import Constants from '../../../Constants';
import { convertToCamelCase } from '../../../helpers';
import SelectField from '../../basic/SelectField';
import DateTimeWithTrappedFocus from '../../core/DateTimeWithTrappedFocus';
import InternationalOrderDetails from '../order-form/edit-form/InternationalOrderDetails/InternationalOrderDetails';
import { Tooltip } from '@/components/ui/tooltip';
import CustomFormControl from '../../core/CustomFormControl';
import './order-table-modal.scss';
import { findEntitiesInState } from '../../../slices/masterData/entityManagerSlice';
import FastInput from '../../basic/FastInput';
import { getTenantFromDomain } from '../../../utils';

const tenant = getTenantFromDomain();

const CustomFormControlWrapper = ({ children, ...rest }) => {
  return <CustomFormControl {...rest}>{children}</CustomFormControl>;
};

CustomFormControlWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const OrderDetailsTab = props => {
  const [fieldErrors, setFieldErrors] = React.useState({});
  const {
    editing,
    isEditableGrinderPoNumber,
    purchaseOrder,
    handleSelectValue,
    sortedPackerOptions,
    sortedPackerPlantOptionsByPacker,
    allPackerPlants,
    isPackerUser,
    packerOptions,
    endUserOptions,
    buyerOptions,
    transporterOptions,
    dropTrailerOptions,
    tagOptions,
    purchaseOrderCameled,
    isGrinderUser,
    isFrozenProduct,
    isUSADomestic,
    deliveryTime,
    shippingDateInputProps,
    deliveryDateInputProps,
    packerDeliveryDateInputProps,
    allPackers,
    users,
    user,
    baseDateInputProps,
    productionDateInputProps,
    setState,
    handleChangeValue,
  } = props;
  const [freightRate, packerPlants] = useSelector(state => {
    const { 0: f, 1: p } = findEntitiesInState(state, ['freight_rate', 'packer_plant']);
    return [f, p];
  });

  const initialDebouncedFields = {
    packer_so_number: purchaseOrder.packer_so_number || '',
    packer_invoice_number: purchaseOrder.packer_invoice_number || '',
    transport_cost_per_unit: purchaseOrder.transport_cost_per_unit || '',
    additinal_grn: purchaseOrder.additinal_grn || '',
    entrapment: purchaseOrder.entrapment || '',
    cartons_exposed_meat: purchaseOrder.cartons_exposed_meat || '',
    cartons_glue_stuck_to_liner: purchaseOrder.cartons_glue_stuck_to_liner || '',
    truck_load_no: purchaseOrder.truck_load_no || '',
  };

  function debouncedFieldsReducer(state, action) {
    switch (action.type) {
      case 'SET_FIELD':
        return { ...state, [action.field]: action.value };
      case 'RESET':
        return { ...initialDebouncedFields, ...action.payload };
      default:
        return state;
    }
  }

  const [debouncedFields, dispatchDebouncedFields] = useReducer(debouncedFieldsReducer, initialDebouncedFields);

  useEffect(() => {
    dispatchDebouncedFields({
      type: 'RESET',
      payload: {
        packer_so_number: purchaseOrder.packer_so_number || '',
        packer_invoice_number: purchaseOrder.packer_invoice_number || '',
        transport_cost_per_unit: purchaseOrder.transport_cost_per_unit || '',
        additinal_grn: purchaseOrder.additinal_grn || '',
        entrapment: purchaseOrder.entrapment || '',
        cartons_exposed_meat: purchaseOrder.cartons_exposed_meat || '',
        cartons_glue_stuck_to_liner: purchaseOrder.cartons_glue_stuck_to_liner || '',
        truck_load_no: purchaseOrder.truck_load_no || '',
      },
    });
  }, [
    purchaseOrder.packer_so_number,
    purchaseOrder.packer_invoice_number,
    purchaseOrder.transport_cost_per_unit,
    purchaseOrder.additinal_grn,
    purchaseOrder.entrapment,
    purchaseOrder.cartons_exposed_meat,
    purchaseOrder.cartons_glue_stuck_to_liner,
    purchaseOrder.truck_load_no,
  ]);

  const getMaxAllowedValue = (rawSellQuantity, sellUOM) => {
    if (!rawSellQuantity || rawSellQuantity <= 0) {
      return 0;
    }
    const uomLower = sellUOM?.toLowerCase();
    if (uomLower === 'lbs' || uomLower === 'lb') {
      return rawSellQuantity / 60;
    }
    if (uomLower === 'kgs' || uomLower === 'kg') {
      return rawSellQuantity / 27.2;
    }
    return rawSellQuantity;
  };

  const handleDebouncedFieldChange = field => e => {
    const value = e.target.value;
    const maxAllowedValue = getMaxAllowedValue(purchaseOrder.buy_quantity, purchaseOrder.buy_unit_of_measure?.name);

    if (
      ['entrapment', 'cartons_exposed_meat', 'cartons_glue_stuck_to_liner'].includes(field) &&
      Number(value) > maxAllowedValue
    ) {
      const fieldLabels = {
        entrapment: 'Entrapment',
        cartons_exposed_meat: 'Number of Cartons with Exposed Meat',
        cartons_glue_stuck_to_liner: 'Number of Cartons with Liner Stuck to Glue',
      };
      setFieldErrors(prev => ({
        ...prev,
        [field]: `More than Load Total Carton Count not allowed`,
      }));
      return;
    }

    setFieldErrors(prev => ({
      ...prev,
      [field]: null,
    }));

    dispatchDebouncedFields({
      type: 'SET_FIELD',
      field,
      value,
    });
  };

  const prevDebouncedFieldsRef = React.useRef(debouncedFields);

  useEffect(() => {
    const hasBlockingErrors = ['entrapment', 'cartons_exposed_meat', 'cartons_glue_stuck_to_liner']
      .some(field => fieldErrors[field]);

    if (hasBlockingErrors) return;
    const changedFields = Object.entries(debouncedFields).filter(
      ([field, value]) => prevDebouncedFieldsRef.current[field] !== value
    );

    const timeouts = changedFields.map(([field, value]) =>
      setTimeout(() => {
        // Convert empty strings to null for numeric fields, otherwise convert to number
        let finalValue = value;
        if (['entrapment', 'cartons_exposed_meat', 'cartons_glue_stuck_to_liner'].includes(field)) {
          if (value === '') {
            finalValue = null;
          } else {
            finalValue = Number(value);
          }
        }
        handleSelectValue(field, { target: { value: finalValue } });
      }, 300)
    );

    prevDebouncedFieldsRef.current = debouncedFields;

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [debouncedFields, handleSelectValue]);

  const onPackerPlantChange = e => {
    const editedPendingOrder = cloneDeep(purchaseOrder);

    editedPendingOrder.packer_plant_id = e ? e.value : null;
    const estPackerPlant = packerPlants.find(p => p.id === e.value);
    // Only update estId if a valid packer plant is found
    if (estPackerPlant) {
      editedPendingOrder.estId = estPackerPlant.est;
      editedPendingOrder.packerPlantId = estPackerPlant.id;
      editedPendingOrder.packer_id = estPackerPlant.packer_id || estPackerPlant.packerId;
    }

    const transportList = freightRate || [];
    const transportCost = transportList.find(item => {
      return (
        item?.grinder_uid === editedPendingOrder?.grinder_uid &&
        item?.packer_plant_id === editedPendingOrder?.packer_plant_id
      );
    });

    if (transportCost) {
      editedPendingOrder.transport_cost_per_unit = transportCost.cost.toFixed(4);
      editedPendingOrder.transport_cost = editedPendingOrder.buy_quantity * editedPendingOrder.transport_cost_per_unit;
      editedPendingOrder.all_in_cost = editedPendingOrder.price + editedPendingOrder.transport_cost;
    } else {
      editedPendingOrder.transport_cost_per_unit = '';
    }

    // >>> GHP recompute block <<<
    if (editedPendingOrder.grinder_uid && editedPendingOrder.delivery_date) {
      const poDeliveryDate = new Date(editedPendingOrder.delivery_date);
      const inputProducts = props.input_products || [];

      editedPendingOrder.lines = (editedPendingOrder.lines || []).map(line => {
        const updatedLine = { ...line };

        const productUid =
          updatedLine.inputProductUid ||
          updatedLine.input_product_uid ||
          updatedLine.inputProduct?.uid ||
          updatedLine.input_product?.uid;

        const product = inputProducts.find(prdct => prdct.uid === productUid);

        if (product) {
          const matchingConfig = (props.groupHousedPremiumConfigs || []).find(cfg => {
            return (
              cfg?.packerPlantId === editedPendingOrder?.packer_plant_id &&
              cfg?.grinderUid === editedPendingOrder?.grinder_uid &&
              cfg?.productCategory === product?.category &&
              cfg?.productSubCategory === product?.sub_category &&
              new Date(cfg.deliveryDateFrom) <= poDeliveryDate
            );
          });

          const ghpCost = matchingConfig ? matchingConfig.cost : 0;
          updatedLine.groupHousedPremiumRate = ghpCost;
          updatedLine.group_housed_premium_rate = ghpCost;
        } else {
          updatedLine.groupHousedPremiumRate = 0;
          updatedLine.group_housed_premium_rate = 0;
        }

        return updatedLine;
      });
    }

    setState({ purchaseOrder: editedPendingOrder, orderChanged: true });
  };

  return (
    <VStack align="stretch">
      <Grid gap="18px" templateColumns="repeat(4, 1fr)" width="100%">
        <CustomFormControlWrapper
          key="CustomFormControlGrinderPO"
          label={
            <>
              Grinder PO#&nbsp;
              <Tooltip
                content="Displays the purchase order number assigned by the grinder."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <FastInput
            id="grinderPO"
            name="grinderPoNumber"
            type="text"
            disabled={!editing || !isEditableGrinderPoNumber}
            className="form-control"
            value={purchaseOrder.grinder_po_number || ''}
            onChange={value => handleChangeValue({ value, target: { value, name: 'grinder_po_number' } })}
          />
        </CustomFormControlWrapper>

        {purchaseOrder?.status === Constants.ORDER_STATUSES.DRAFT && (
          <SelectField
            key="CustomFormControlProductType"
            label="Product Type"
            name="product-type"
            value={
              Constants.PRODUCT_TYPES.find(option => option.value === purchaseOrder.productType) ||
              Constants.PRODUCT_TYPE_LIST.BEEF
            }
            options={Constants.PRODUCT_TYPES}
            placeholder="Product Type"
            disabled={!editing}
            onChange={e => handleSelectValue('productType', e)}
          />
        )}

        <SelectField
          label={
            <>
              End User&nbsp;
              <Tooltip
                content="Identifies the final recipient to ensure the product meets specific requirements."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          name="select-end-user"
          value={endUserOptions.find(e => e.value === purchaseOrder.end_user_id)}
          options={endUserOptions}
          placeholder="End User"
          disabled={!editing}
          onChange={e => handleSelectValue('end_user_id', e)}
        />

        <SelectField
          key="CustomFormControlPackerId"
          label={
            <>
              Packer&nbsp;
              <Tooltip
                content="Specifies the packer responsible for handling and tracking."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          id="select-packer"
          name="packer_id"
          value={
            (editing ? packerOptions : (allPackers || []).map(el => ({ value: el.id, label: el.name }))).find(
              option => option.value === get(purchaseOrder, 'packer_id')
            ) || null
          }
          options={sortedPackerOptions}
          disabled={!editing}
          // className={requiredFields.form.includes('packerId') ? 'error' : ''}
          onChange={e => handleSelectValue('packer_id', e)}
        />

        <SelectField
          label={
            <>
              Establishment&nbsp;
              <Tooltip
                content="Identifies the establishment associated with the order."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          name="select-packer"
          // defaultValue={purchaseOrder.packer_plant_id}
          value={
            (editing
              ? sortedPackerPlantOptionsByPacker
              : (allPackerPlants || []).map(el => ({ value: el.id, label: el.name }))
            ).find(option => option.value === get(purchaseOrder, 'packer_plant_id')) || null
          }
          options={sortedPackerPlantOptionsByPacker}
          placeholder="Establishment"
          disabled={!editing && !isPackerUser}
          onChange={onPackerPlantChange}
        />

        <CustomFormControlWrapper
          key="CustomFormControlEstId"
          label={
            <>
              Establishment Number&nbsp;
              <Tooltip
                content="Records the official number for traceability and compliance."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <FastInput
            id="estId"
            name="estId"
            value={
              editing && allPackerPlants?.find(option => option.id === get(purchaseOrder, 'packer_plant_id'))?.delisted
                ? ''
                : purchaseOrder.estId
            }
            disabled={!editing}
            className="form-control"
            onChange={value => handleSelectValue('estId', { value })}
          />
        </CustomFormControlWrapper>

        {/* <CustomFormControlWrapper
          key="CustomFormControlExpectedTestingFee"
          label={
            <>
              Expected Testing Fee&nbsp;
              <Tooltip
                content="Records the Expected Testing Fee."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <Input
            id="expectedTestingFee"
            name="expectedTestingFee"
            value={
              editing && allPackerPlants?.find(option => option.id === get(purchaseOrder, 'expected_testing_fee'))
                ? ''
                : purchaseOrder.expectedTestingFee
            }
            disabled={!editing}
            className="form-control"
            onChange={e => handleSelectValue('expectedTestingFee', e)}
          />
        </CustomFormControlWrapper> */}

        <CustomFormControlWrapper
          key="CustomFormControlFreightRate"
          label={
            <>
              Freight Rate&nbsp;
              <Tooltip
                content="Indicates the cost of transporting goods to aid in logistics planning."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <Input
            id="freight_rate"
            name="transport_cost_per_unit"
            type="number"
            onWheelCapture={e => {
              e.target.blur();
            }}
            disabled={!editing}
            className="form-control"
            value={debouncedFields.transport_cost_per_unit || ''}
            onChange={e => handleDebouncedFieldChange('transport_cost_per_unit')(e)}
          />
        </CustomFormControlWrapper>

        <SelectField
          key="CustomFormControlBuyerId"
          label={
            <>
              Buyer&nbsp;
              <Tooltip
                content="Identifies the individual or organization purchasing the product."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          id="select-buyer"
          name="buyer_id"
          defaultValue={purchaseOrder.buyer_id}
          // value={buyerOptions.find(option => option.value === get(form, 'buyer_id')) || null}
          options={buyerOptions}
          disabled={!editing}
          // className={requiredFields.form.includes('buyer_id') ? 'error' : ''}
          onChange={e => handleSelectValue('buyer_id', e)}
          isClearable={false}
        />

        {/* Check was wrong, Shipment date should only show for Domestic(US) */}
        {isUSADomestic && purchaseOrder?.status === Constants.ORDER_STATUSES.DRAFT && (
          <CustomFormControlWrapper label="Shipment Date">
            <DateTimeWithTrappedFocus
              id="shipment_date"
              minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
              inputProps={shippingDateInputProps}
              closeOnSelect
              value={purchaseOrder.shipment_date}
              onChange={e => handleSelectValue('shipment_date', e)}
              timeFormat={false}
              dateFormat="YYYY-MM-DD"
            />
          </CustomFormControlWrapper>
        )}

        <CustomFormControlWrapper label={<>{isUSADomestic && !isFrozenProduct ? 'Scheduled' : ''} Delivery Date</>}>
          <DateTimeWithTrappedFocus
            id="delivery_date"
            minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
            inputProps={deliveryDateInputProps}
            closeOnSelect
            value={purchaseOrder.delivery_date}
            onChange={e => handleSelectValue('delivery_date', e)}
            timeFormat={false}
            dateFormat="YYYY-MM-DD"
            className="full-width"
          />
        </CustomFormControlWrapper>
        <Box>
          <label htmlFor="delivery_time">Delivery Time</label>
          <Input
            type="time"
            id="delivery_time"
            value={deliveryTime}
            onChange={e => handleSelectValue('scheduled_delivery_time', e)}
            disabled={!editing}
          />
        </Box>
        <CustomFormControlWrapper label="Packer Delivery Date">
          <DateTimeWithTrappedFocus
            id="packer_delivery_date"
            minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
            inputProps={packerDeliveryDateInputProps}
            closeOnSelect
            value={purchaseOrder.packer_delivery_date}
            onChange={e => handleSelectValue('packer_delivery_date', e)}
            timeFormat={false}
            dateFormat="YYYY-MM-DD"
            className="full-width"
          />
        </CustomFormControlWrapper>
        {isUSADomestic && !isFrozenProduct && (
          <CustomFormControlWrapper label="Grinder Received Date">
            <DateTimeWithTrappedFocus
              id="recv_delivery_date"
              minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
              inputProps={{
                ...baseDateInputProps,
                placeholder: 'Grinder Received Date',
              }}
              closeOnSelect
              value={purchaseOrder.recv_delivery_date}
              onChange={e => handleSelectValue('recv_delivery_date', e)}
              timeFormat={false}
              dateFormat="YYYY-MM-DD"
              className="full-width"
            />
          </CustomFormControlWrapper>
        )}

        {/* Check was wrong it should only show for Domestic(US) */}
        {isUSADomestic && purchaseOrder?.status === Constants.ORDER_STATUSES.DRAFT && (
          <CustomFormControlWrapper label="Production Date">
            <DateTimeWithTrappedFocus
              id="production_date"
              minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
              inputProps={productionDateInputProps}
              closeOnSelect
              value={purchaseOrder.production_date}
              onChange={e => handleSelectValue('production_date', e)}
              timeFormat={false}
              dateFormat="YYYY-MM-DD"
              className="full-width"
            />
          </CustomFormControlWrapper>
        )}

        <CustomFormControlWrapper
          label={
            <>
              Packer SO#&nbsp;
              <Tooltip
                content="Displays the sales order number assigned by the packer."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <Input
            id="packer_so_number"
            className="form-control"
            type="text"
            disabled={!editing && !isPackerUser}
            placeholder="Packer SO#"
            onChange={e => handleDebouncedFieldChange('packer_so_number')(e)}
            value={debouncedFields.packer_so_number || ''}
          />
        </CustomFormControlWrapper>
        {isUSADomestic && !isFrozenProduct && (
          <>
            <CustomFormControlWrapper
              label={
                <>
                  Packer Paid Date&nbsp;
                  <Tooltip
                    content="Displays the date when the packer made the payment."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <DateTimeWithTrappedFocus
                id="packer_paid_date"
                minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
                inputProps={{
                  ...baseDateInputProps,
                  placeholder: 'Packer Paid Date',
                }}
                closeOnSelect
                value={purchaseOrder?.packer_paid_date}
                onChange={e => handleSelectValue('packer_paid_date', e)}
                timeFormat={false}
                dateFormat="YYYY-MM-DD"
                className="full-width"
              />
            </CustomFormControlWrapper>
            <CustomFormControlWrapper
              label={
                <>
                  Packer Invoice#&nbsp;
                  <Tooltip
                    content="Displays the invoice number assigned by the packer."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <Input
                id="packer_invoice_number"
                className="form-control"
                type="text"
                disabled={!editing && !isPackerUser}
                placeholder="Packer Invoice#"
                onChange={e => handleDebouncedFieldChange('packer_invoice_number')(e)}
                value={debouncedFields.packer_invoice_number || ''}
              />
            </CustomFormControlWrapper>
            <CustomFormControlWrapper label="Packer Invoice Date">
              <DateTimeWithTrappedFocus
                id="packer_invoice_date"
                minDate={new Date(moment().subtract(7, 'day').format('YYYY/MM/DD'))}
                inputProps={{
                  ...baseDateInputProps,
                  placeholder: 'Packer Invoice Date',
                }}
                closeOnSelect
                value={purchaseOrder.packer_invoice_date}
                onChange={e => handleSelectValue('packer_invoice_date', e)}
                timeFormat={false}
                dateFormat="YYYY-MM-DD"
                className="full-width"
              />
            </CustomFormControlWrapper>

            <SelectField
              label="Tags"
              {...{
                className: 'edit-international-order__select',
                isClearable: true,
                disabled: !editing,
                isSearchable: true,
                closeMenuOnSelect: false,
                removeSelected: true,
                isMulti: true,
                name: 'select-tags',
                value: purchaseOrder.tags ? purchaseOrder.tags.map(t => ({ label: t, value: t })) : [],
                onChange: e => handleSelectValue('tags', e),
                options: tagOptions?.sort((a, b) => (a.label > b.label ? 1 : -1)),
              }}
            />

            <CustomFormControlWrapper label="Additional Reference">
              <Input
                id="additinal_grn"
                className="form-control"
                type="text"
                disabled={!editing && !isPackerUser}
                placeholder="Additional Reference"
                onChange={e => handleDebouncedFieldChange('additinal_grn')(e)}
                value={debouncedFields.additinal_grn || ''}
              />
            </CustomFormControlWrapper>
            <CustomFormControlWrapper label="Entrapment">
              <Input
                id="entrapment"
                className="form-control"
                type="text"
                disabled={!editing && !isPackerUser}
                placeholder="Entrapment"
                onChange={e => handleDebouncedFieldChange('entrapment')(e)}
                value={debouncedFields.entrapment || ''}
              />
              {fieldErrors.entrapment && (
                <Text color="red.500" fontSize="sm" mt="2">
                  {fieldErrors.entrapment}
                </Text>
              )}
            </CustomFormControlWrapper>
          </>
        )}
      </Grid>
      {tenant !== 'cfc' && isUSADomestic && !isFrozenProduct && (
        <Grid gap="18px" templateColumns="repeat(4, 1fr)" width="100%">
          <Box>
            <CustomFormControlWrapper label="Number of Cartons with Exposed Meat" key="cartons_exposed_meat">
              <Input
                id="cartons_exposed_meat"
                className="form-control"
                type="number"
                disabled={!editing && !isPackerUser}
                placeholder="Number of Cartons with Exposed Meat"
                onChange={e => handleDebouncedFieldChange('cartons_exposed_meat')(e)}
                value={debouncedFields.cartons_exposed_meat || ''}
                borderColor={fieldErrors.cartons_exposed_meat ? 'red.500' : undefined}
              />
              {fieldErrors.cartons_exposed_meat && (
                <Text color="red.500" fontSize="sm" mt="2">
                  {fieldErrors.cartons_exposed_meat}
                </Text>
              )}
            </CustomFormControlWrapper>
          </Box>
          <Box>
            <CustomFormControlWrapper label="Number of Cartons with Liner Stuck to Glue" key="cartons_glue_stuck_to_liner">
              <Input
                id="cartons_glue_stuck_to_liner"
                className="form-control"
                type="number"
                disabled={!editing && !isPackerUser}
                placeholder="Number of Cartons with Liner Stuck to Glue"
                onChange={e => handleDebouncedFieldChange('cartons_glue_stuck_to_liner')(e)}
                value={debouncedFields.cartons_glue_stuck_to_liner || ''}
                borderColor={fieldErrors.cartons_glue_stuck_to_liner ? 'red.500' : undefined}
              />
              {fieldErrors.cartons_glue_stuck_to_liner && (
                <Text color="red.500" fontSize="sm" mt="2">
                  {fieldErrors.cartons_glue_stuck_to_liner}
                </Text>
              )}
            </CustomFormControlWrapper>
          </Box>
        </Grid>
      )}
      {/* Condition was wrong, As we need to show these fields for Domestic(US) only not for Internation (AU/NZ) */}
      {isUSADomestic && purchaseOrder?.status === Constants.ORDER_STATUSES.DRAFT && (
        <Grid gap="18px" templateColumns="repeat(4, 1fr)" width="100%">
          {!isGrinderUser && (
            <CustomFormControlWrapper label="Freight Rate">
              <Input
                backgroundColor="white"
                id="freight_rate"
                className="form-control"
                type="number"
                disabled={!editing && !isPackerUser}
                onWheelCapture={e => {
                  e.target.blur();
                }}
                placeholder="Freight rate"
                value={debouncedFields.transport_cost_per_unit || ''}
                onChange={e => handleDebouncedFieldChange('transport_cost_per_unit')(e)}
              />
            </CustomFormControlWrapper>
          )}

          <SelectField
            label="Carrier"
            name="select-carrier"
            disabled={!editing}
            value={transporterOptions.find(opt => opt.value === purchaseOrder.transporter_id)}
            options={transporterOptions}
            placeholder="Carrier"
            onChange={e => handleSelectValue('transporter_id', e)}
          />

          <SelectField
            label="Drop Trailer"
            name="drop_trailer"
            disabled={!editing}
            value={dropTrailerOptions.find(trailer => trailer.value === (purchaseOrder.drop_trailer || ''))}
            options={dropTrailerOptions}
            placeholder="Drop Trailer"
            onChange={e => handleSelectValue('drop_trailer', e)}
          />

          <CustomFormControlWrapper label="Truck Load Number">
            <Input
              backgroundColor="white"
              id="truck_load_no"
              className="form-control"
              type="text"
              disabled={!editing && !isPackerUser}
              placeholder="Truck Load Number"
              onChange={e => handleDebouncedFieldChange('truck_load_no')(e)}
              value={debouncedFields.truck_load_no || ''}
            />
          </CustomFormControlWrapper>
        </Grid>
      )}
      {isUSADomestic && !isFrozenProduct && (
        <InternationalOrderDetails
          form={purchaseOrderCameled}
          requiredFields={{
            form: [],
          }}
          editMode={editing}
          user={user}
          users={users}
          {...convertToCamelCase(props)}
          handleChangeValue={(name, e) => handleChangeValue({ target: { name, ...e } })}
        />
      )}
    </VStack>
  );
};

export default OrderDetailsTab;

OrderDetailsTab.propTypes = {
  editing: PropTypes.bool.isRequired,
  isEditableGrinderPoNumber: PropTypes.bool.isRequired,
  purchaseOrder: PropTypes.shape({
    grinder_po_number: PropTypes.string,
    productType: PropTypes.string,
    end_user_id: PropTypes.string,
    packer_id: PropTypes.string,
    packer_plant_id: PropTypes.string,
    estId: PropTypes.string,
    // expected_testing_fee: PropTypes.string,
    transport_cost_per_unit: PropTypes.string,
    buyer_id: PropTypes.string,
    shipment_date: PropTypes.string,
    delivery_date: PropTypes.string,
    scheduled_delivery_time: PropTypes.string,
    packer_delivery_date: PropTypes.string,
    packer_paid_date: PropTypes.string,
    packer_so_number: PropTypes.string,
    packer_invoice_number: PropTypes.string,
    packer_invoice_date: PropTypes.string,
    recv_delivery_date: PropTypes.string,
    production_date: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    additinal_grn: PropTypes.string,
    entrapment: PropTypes.string,
    cartons_exposed_meat: PropTypes.number,
    cartons_glue_stuck_to_liner: PropTypes.number,
    truck_load_no: PropTypes.string,
    drop_trailer: PropTypes.string,
    transporter_id: PropTypes.string,
    status: PropTypes.string,
    product_type: PropTypes.string,
  }),
  handleSelectValue: PropTypes.func.isRequired,
  sortedPackerOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sortedPackerPlantOptionsByPacker: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allPackerPlants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isPackerUser: PropTypes.bool.isRequired,
  packerOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  endUserOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  buyerOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  transporterOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dropTrailerOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tagOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  purchaseOrderCameled: PropTypes.shape({}),
  isGrinderUser: PropTypes.bool.isRequired,
  isFrozenProduct: PropTypes.bool.isRequired,
  isUSADomestic: PropTypes.bool.isRequired,
  deliveryTime: PropTypes.string.isRequired,
  shippingDateInputProps: PropTypes.shape({}),
  deliveryDateInputProps: PropTypes.shape({}),
  packerDeliveryDateInputProps: PropTypes.shape({}),
  allPackers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({}).isRequired,
  baseDateInputProps: PropTypes.shape({}),
  productionDateInputProps: PropTypes.shape({}),
  setState: PropTypes.func.isRequired,
  handleChangeValue: PropTypes.func,
};
